import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function cellStyle(pnl, maxAbs) {
  if (pnl === undefined) return { background: "rgba(255,255,255,0.03)" };
  if (pnl === 0) return { background: "rgba(148,163,184,0.25)" };
  const intensity = 0.25 + 0.75 * Math.min(1, Math.abs(pnl) / (maxAbs || 1));
  return { background: pnl > 0 ? `rgba(16,185,129,${intensity})` : `rgba(239,68,68,${intensity})` };
}

export const PnlCalendar = ({ daily = [] }) => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [hover, setHover] = useState(null);

  const byDate = useMemo(() => Object.fromEntries(daily.map((d) => [d.date, d])), [daily]);
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  const monthDays = daily.filter((d) => d.date.startsWith(monthKey));
  const maxAbs = Math.max(0, ...monthDays.map((d) => Math.abs(d.pnl)));
  const monthPnl = Math.round(monthDays.reduce((s, d) => s + d.pnl, 0) * 100) / 100;
  const greenDays = monthDays.filter((d) => d.pnl > 0).length;

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${monthKey}-${String(d).padStart(2, "0")}`);

  return (
    <div className="glass rounded-2xl p-6" data-testid="pnl-calendar-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-emerald-400" /> P&L Calendar</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-1.5 rounded-md hover:bg-white/10 text-slate-300 transition-colors" data-testid="pnl-calendar-prev" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-mono-num text-sm w-28 text-center" data-testid="pnl-calendar-month">{cursor.toLocaleString("en", { month: "short", year: "numeric" })}</span>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-1.5 rounded-md hover:bg-white/10 text-slate-300 transition-colors" data-testid="pnl-calendar-next" aria-label="Next month"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Month P&L <span className={`font-mono-num font-semibold ${monthPnl >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="pnl-calendar-month-total">{monthPnl >= 0 ? "+" : ""}{monthPnl}</span>
        {" · "}{greenDays}/{monthDays.length} green days
      </p>
      <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
        {DOW.map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((key, i) => {
          if (!key) return <div key={`e${i}`} />;
          const day = byDate[key];
          return (
            <div key={key} data-testid={`pnl-day-${key}`} onMouseEnter={() => setHover(key)} onMouseLeave={() => setHover(null)}
              title={day ? `${key}: ${day.pnl >= 0 ? "+" : ""}${day.pnl} · ${day.trades} trade${day.trades > 1 ? "s" : ""}` : key}
              className={`relative aspect-square rounded-md flex items-center justify-center text-[11px] font-mono-num transition-transform hover:scale-105 cursor-default ${day ? "text-white font-semibold" : "text-slate-600"}`}
              style={cellStyle(day?.pnl, maxAbs)}>
              {Number(key.slice(-2))}
              {hover === key && day && (
                <div className="absolute z-10 bottom-full mb-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/10 bg-[#121824] px-2.5 py-1.5 text-xs text-left shadow-xl" data-testid="pnl-day-tooltip">
                  <p className={`font-mono-num font-semibold ${day.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>{day.pnl >= 0 ? "+" : ""}{day.pnl}</p>
                  <p className="text-slate-400">{day.trades} trade{day.trades > 1 ? "s" : ""} · {day.wins} win{day.wins !== 1 ? "s" : ""}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-4 text-[10px] text-slate-500">
        <span>Loss</span>
        <div className="flex gap-1">{[1, 0.6, 0.3].map((a) => <span key={`l${a}`} className="w-4 h-3 rounded-sm" style={{ background: `rgba(239,68,68,${a})` }} />)}
          <span className="w-4 h-3 rounded-sm" style={{ background: "rgba(255,255,255,0.05)" }} />
          {[0.3, 0.6, 1].map((a) => <span key={`g${a}`} className="w-4 h-3 rounded-sm" style={{ background: `rgba(16,185,129,${a})` }} />)}</div>
        <span>Profit</span>
      </div>
    </div>
  );
};
